import React, { useState, useEffect } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/content_wrapper/header";
import { useGetKeyDetails } from "../../hooks/keyManagement/keyDetail";
import { useGetKeyHistory } from "../../hooks/keyManagement/keyHistoty";
import { useCancelSubscription } from "../../hooks/keyManagement/cancelSubscription";
import {
  useAdminCancelSubscription,
  useAdminReactivateSubscription,
  useAdminCompKey,
  useAdminPayOutstanding,
} from "../../hooks/keyManagement/adminKeySubscription";
import { useGetCollectionCodes } from "../../hooks/collectionCodes/getCollectionCodes";
import { useGetCollectionCodeDetails } from "../../hooks/collectionCodes/getCollectionCodeDetails";
import { useDeleteKey } from "../../hooks/keyManagement/deleteKey";
import { useChangeKeyAuthorizedHost } from "../../hooks/keyManagement/useChangeKeyAuthorizedHost";
import { useGetKeyHost } from "../../hooks/keyHostManagementApi/getKeyHost";
import getSymbolFromCurrency from "currency-symbol-map";
import CustomLaoder from "../../components/customLoader";

type TabId = "transactions" | "activity" | "codes";

const KeyManagementDetailScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state;
  const keyId = data?.id;

  // Redirect to listing if navigated directly without state
  useEffect(() => {
    if (!keyId) {
      navigate("/keymanagementlisting", { replace: true });
    }
  }, [keyId, navigate]);

  const { data: keyDetail, isLoading, isError, error } = useGetKeyDetails({ id: keyId });
  const keyDetailData = keyDetail?.data?.keyDetails;

  // Key History
  const { data: keyHistory } = useGetKeyHistory({ id: keyId });

  // Tab state — default to collection activity
  const [activeTab, setActiveTab] = useState<TabId>("activity");

  // Collection Codes (New System)
  const [collectionCodesData, setCollectionCodesData] = useState<any>(null);
  const [codesEverLoaded, setCodesEverLoaded] = useState(false);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCodes, setFilteredCodes] = useState<any[]>([]);
  const { getCollectionCodes } = useGetCollectionCodes();
  const { getCollectionCodeDetails: _getCollectionCodeDetails } = useGetCollectionCodeDetails();

  const { mutateAsync } = useCancelSubscription();
  const { mutateAsync: adminCancelAsync, isLoading: adminCancelLoading } =
    useAdminCancelSubscription();
  const { mutateAsync: reactivateAsync, isLoading: reactivateLoading } =
    useAdminReactivateSubscription();
  const { mutateAsync: compAsync, isLoading: compLoading } = useAdminCompKey();
  const { mutateAsync: payOutstandingAsync, isLoading: payOutstandingLoading } =
    useAdminPayOutstanding();
  const [compDays, setCompDays] = useState("7");

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [keyHostNotified, setKeyHostNotified] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const { mutate: deleteKey } = useDeleteKey();

  const openDeleteModal = () => {
    setDeleteReason("");
    setKeyHostNotified(false);
    setShowDeleteModal(true);
  };
  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setShowDeleteModal(false);
    setDeleteReason("");
    setKeyHostNotified(false);
  };
  const handleConfirmDelete = () => {
    if (!deleteReason.trim()) { toast.error("Please provide a reason for deletion"); return; }
    if (keyDetailData?.status === 1 && !keyHostNotified) { toast.error("Please confirm the key host has been notified"); return; }
    setDeleteSubmitting(true);
    deleteKey(
      {
        key_id: keyId,
        reason: deleteReason.trim(),
        ...(keyDetailData?.status === 1 && { key_host_notified: true }),
      },
      {
        onSuccess: () => {
          toast.success("Key deleted successfully");
          setDeleteSubmitting(false);
          setShowDeleteModal(false);
          navigate("/keymanagementlisting");
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || "Failed to delete key");
          setDeleteSubmitting(false);
        },
      }
    );
  };

  // Change Authorized Host modal
  const [showChangeHostModal, setShowChangeHostModal] = useState(false);
  const [changeHostSearch, setChangeHostSearch] = useState("");
  const [selectedHostId, setSelectedHostId] = useState("");
  const [changeHostReason, setChangeHostReason] = useState("");
  const [changeHostSubmitting, setChangeHostSubmitting] = useState(false);
  const { mutate: changeAuthorizedHost } = useChangeKeyAuthorizedHost();
  const [debouncedChangeHostSearch, setDebouncedChangeHostSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedChangeHostSearch(changeHostSearch), 400);
    return () => clearTimeout(timer);
  }, [changeHostSearch]);
  const { data: keyHostOptions } = useGetKeyHost({
    page: "1",
    limit: "50",
    search: debouncedChangeHostSearch,
    enabled: showChangeHostModal,
  });

  const openChangeHostModal = () => {
    setChangeHostSearch("");
    setSelectedHostId("");
    setChangeHostReason("");
    setShowChangeHostModal(true);
  };
  const closeChangeHostModal = () => {
    if (changeHostSubmitting) return;
    setShowChangeHostModal(false);
  };
  const handleConfirmChangeHost = () => {
    if (!selectedHostId) { toast.error("Please select a key host"); return; }
    if (!changeHostReason.trim()) { toast.error("Please provide a reason for this change"); return; }
    setChangeHostSubmitting(true);
    changeAuthorizedHost(
      {
        key_id: keyId,
        key_host_id: selectedHostId,
        reason: changeHostReason.trim(),
      },
      {
        onSuccess: () => {
          toast.success("Authorized host changed successfully");
          setChangeHostSubmitting(false);
          setShowChangeHostModal(false);
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || "Failed to change authorized host");
          setChangeHostSubmitting(false);
        },
      }
    );
  };

  // Load collection codes lazily on first tab activation
  const loadCollectionCodes = async (page: number = 1, status: string = "") => {
    setLoadingCodes(true);
    try {
      const params: any = { keyId: keyId, page, limit: 20 };
      if (status) params.status = status;
      const response = await getCollectionCodes(params);
      setCollectionCodesData(response);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error loading collection codes:", error);
    } finally {
      setLoadingCodes(false);
    }
  };

  // Activate a tab; lazily load codes on first visit
  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    if (tab === "codes" && !codesEverLoaded) {
      setCodesEverLoaded(true);
      loadCollectionCodes(1, "");
    }
  };

  // Client-side search filter for collection codes
  useEffect(() => {
    if (collectionCodesData?.data?.codes) {
      if (searchQuery.trim() === "") {
        setFilteredCodes(collectionCodesData.data.codes);
      } else {
        const q = searchQuery.toLowerCase();
        setFilteredCodes(
          collectionCodesData.data.codes.filter(
            (code: any) =>
              code.code.toLowerCase().includes(q) ||
              (code.label && code.label.toLowerCase().includes(q))
          )
        );
      }
    }
  }, [searchQuery, collectionCodesData]);

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setSearchQuery("");
    loadCollectionCodes(1, status);
  };

  const handlePageChange = (newPage: number) => {
    loadCollectionCodes(newPage, statusFilter);
  };

  const handleToggleClick = async (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to cancel this key's subscription?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#72bcb8",
      cancelButtonColor: "#d33",
      cancelButtonText: "Back",
      confirmButtonText: "Yes, Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res: any = await mutateAsync({ key_id: id });
          if (res?.statusCode === 200) {
            toast.success(res?.message);
          }
        } catch (err: any) {
          toast.error(err?.response?.data?.message);
        }
      }
    });
  };

  // Phase 0 non-destructive levers. "Cancel Subscription" above is the legacy
  // immediate Stripe cancel; these schedule a cancel-at-period-end, retry a
  // Stripe cancel that failed during retirement, or undo a scheduled cancel.
  const runAdminLever = (
    fn: () => Promise<any>,
    { title, text, confirmText }: { title: string; text: string; confirmText: string }
  ) => {
    Swal.fire({
      title,
      text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#72bcb8",
      cancelButtonColor: "#d33",
      cancelButtonText: "Back",
      confirmButtonText: confirmText,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res: any = await fn();
        toast.success(res?.message || "Done");
      } catch (err: any) {
        toast.error(
          err?.displayMessage ||
            err?.response?.data?.message ||
            "The operation failed"
        );
      }
    });
  };

  const handleScheduleCancel = () =>
    runAdminLever(() => adminCancelAsync({ keyId: keyDetailData?._id }), {
      title: "Schedule cancellation?",
      text: "The subscription will cancel at the end of the paid period and the owner return code will be issued. Not the same as the immediate cancel.",
      confirmText: "Schedule cancel",
    });

  const handleResolveStuckCancel = () =>
    runAdminLever(() => adminCancelAsync({ keyId: keyDetailData?._id }), {
      title: "Retry Stripe cancellation?",
      text: "This retirement's Stripe cancel failed. Retry the immediate cancel now and clear the flag.",
      confirmText: "Retry now",
    });

  const handleReactivate = () =>
    runAdminLever(() => reactivateAsync({ keyId: keyDetailData?._id }), {
      title: "Reactivate subscription?",
      text: "Undo the scheduled cancellation and revoke the owner return code. Only works while still inside the paid period.",
      confirmText: "Reactivate",
    });

  const handleComp = () => {
    const days = parseInt(compDays, 10);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.error("Enter a number of days between 1 and 365");
      return;
    }
    runAdminLever(
      () => compAsync({ key_id: keyDetailData?._id, days, reason: "admin comp" }),
      {
        title: `Comp ${days} day(s)?`,
        text: "Pushes the Stripe trial end forward — billing pauses for that long (no proration) and the key goes active. Survives the 6-hourly re-sync.",
        confirmText: "Comp",
      }
    );
  };

  const handlePayOutstanding = () =>
    runAdminLever(
      () => payOutstandingAsync({ key_id: keyDetailData?._id }),
      {
        title: "Pay the outstanding invoice?",
        text: "Charges the card already on file for this key's open invoice, on the owner's behalf. If it needs 3-D Secure the owner must finish it themselves.",
        confirmText: "Pay now",
      }
    );

  // Status pill helpers
  const getStatusInfo = (status: number) => {
    switch (status) {
      case 0: return { label: "Waiting Dropoff", cls: "c-pill--warning" };
      case 1: return { label: "In Keybox",        cls: "c-pill--success" };
      case 2: return { label: "Not In Keybox",    cls: "c-pill--danger"  };
      default: return { label: "Deleted",         cls: "c-pill--danger"  };
    }
  };
  const statusInfo = getStatusInfo(keyDetailData?.status);

  // Subscription stat-card helpers
  const getSubBorderClass = () => {
    if (keyDetailData?.stripeSubscriptionStatus === "canceled" || data?.status === 3) return "border-danger";
    return "border-success";
  };
  const getSubTextClass = () => {
    if (keyDetailData?.stripeSubscriptionStatus === "canceled" || data?.status === 3) return "text-danger";
    return "text-success";
  };
  const getSubLabel = () => {
    if (keyDetailData?.stripeSubscriptionStatus === "canceled") return "Cancelled";
    if (data?.status === 3) return "Deleted";
    return "Active";
  };

  // Show error state if the API call failed
  if (!isLoading && isError) {
    const errMsg = (error as any)?.displayMessage || (error as any)?.response?.data?.message || "Failed to load key details.";
    return (
      <div className="main-wrapper">
        <Header title="Key Detail" />
        <div className="content-wrapper">
          <section className="main-section spacer">
            <div className="container pt-4">
              <button className="btn primary-btn me-0 view-history-btn mb-4" onClick={() => navigate(-1)}>
                <i className="fa-solid fa-chevron-left" />
                <span className="m-lg-1">Back</span>
              </button>
              <div className="alert alert-danger">
                <i className="fa-solid fa-triangle-exclamation me-2" />
                <strong>Could not load key details.</strong> {errMsg}
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  // Tab definitions
  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: "activity",     label: "Collection Activity", icon: "fa-clock-rotate-left" },
    { id: "codes",        label: "Collection Codes",    icon: "fa-qrcode"            },
    { id: "transactions", label: "Transactions",        icon: "fa-receipt"           },
  ];

  return (
    <div className="main-wrapper">
      <Header title="Key Detail" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section profile-section spacer">
            <div className="container">

              {/* ── Back button ── */}
              <button
                className="btn primary-btn me-0 view-history-btn my-3"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-chevron-left" />
                <span className="m-lg-1">Back</span>
              </button>

              {/* ── Page Hero ── */}
              <div className="wrapper-card mb-4 py-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-light"
                      style={{ width: 52, height: 52, flexShrink: 0 }}
                    >
                      <i className="fa-solid fa-key fs-4 text-primary" />
                    </div>
                    <div>
                      <h2 className="mb-1 fw-bold">{keyDetailData?.name || "—"}</h2>
                      <span className={`c-pill ${statusInfo.cls}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm"
                      style={{ background: "#72bcb8", color: "#fff", border: "none" }}
                      onClick={() => navigate("/key-investigation", { state: { id: keyId } })}
                      title="Open investigation panel"
                    >
                      <i className="fa-solid fa-microscope me-2" />
                      Investigate
                    </button>
                    {data?.status !== 3 &&
                      keyDetailData?.stripeSubscriptionStatus !== "canceled" &&
                      !keyDetailData?.subscriptionCancelAtPeriodEnd && (
                        <>
                          <button
                            className="btn btn-outline-warning btn-sm"
                            disabled={adminCancelLoading}
                            onClick={handleScheduleCancel}
                            title="Cancel at end of paid period and issue the owner return code"
                          >
                            <i className="fa-solid fa-clock me-2" />
                            Schedule Cancel
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleToggleClick(keyDetailData?._id)}
                            title="Immediate Stripe cancel (legacy)"
                          >
                            <i className="fa-solid fa-ban me-2" />
                            Cancel Now
                          </button>
                        </>
                      )}
                    {keyDetailData?.status !== 3 &&
                      keyDetailData?.subscriptionCancelAtPeriodEnd && (
                        <button
                          className="btn btn-outline-success btn-sm"
                          disabled={reactivateLoading}
                          onClick={handleReactivate}
                        >
                          <i className="fa-solid fa-rotate-left me-2" />
                          Reactivate Subscription
                        </button>
                      )}
                    {keyDetailData?.status !== 3 && keyDetailData?.status !== 1 && (
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={openChangeHostModal}
                      >
                        <i className="fa-solid fa-shuffle me-2" />
                        Change Authorized Host
                      </button>
                    )}
                    {keyDetailData?.status !== 3 && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={openDeleteModal}
                      >
                        <i className="fa-solid fa-trash me-2" />
                        Delete Key
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Stuck Stripe cancellation banner ── */}
              {keyDetailData?.subscriptionCancelFailedAt && (
                <div className="alert alert-danger d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                  <div>
                    <i className="fa-solid fa-triangle-exclamation me-2" />
                    <strong>Stripe cancellation failed</strong> on{" "}
                    {moment
                      .unix(Number(keyDetailData.subscriptionCancelFailedAt))
                      .format("MMM D, YYYY h:mm A")}
                    . This key was retired but Stripe may still be billing the
                    owner — retry the cancellation to resolve it.
                  </div>
                  <button
                    className="btn btn-danger btn-sm"
                    disabled={adminCancelLoading}
                    onClick={handleResolveStuckCancel}
                  >
                    <i className="fa-solid fa-rotate-right me-2" />
                    Retry Stripe cancellation
                  </button>
                </div>
              )}

              {keyDetailData?.status !== 3 &&
                keyDetailData?.subscriptionCancelAtPeriodEnd && (
                  <div className="alert alert-warning mb-4">
                    <i className="fa-solid fa-clock me-2" />
                    Subscription is scheduled to cancel at the end of the paid
                    period
                    {keyDetailData?.subscriptionCancelledBy
                      ? ` (by ${keyDetailData.subscriptionCancelledBy})`
                      : ""}
                    . The owner return code has been issued. Use{" "}
                    <strong>Reactivate Subscription</strong> to undo this while
                    still inside the paid period.
                  </div>
                )}

              {/* ── Billing status (docs/PAYMENT_PROCESSING_REVIEW.md §8) ── */}
              {(keyDetailData?.billingStatus || keyDetailData?.disputed) && (
                <div className="wrapper-card mb-4">
                  <h3 className="mb-4">
                    <i className="fa-solid fa-credit-card me-2 text-muted" />
                    Billing status
                  </h3>
                  <div className="row gy-3 mb-3">
                    <div className="col-sm-6 col-lg-3">
                      <div className="info-detail">
                        <h5 className="text-muted mb-1">State</h5>
                        <p className="mb-0 fw-semibold text-capitalize">
                          {keyDetailData?.billingStatus || "—"}
                          {keyDetailData?.disputed && (
                            <span className="badge bg-danger ms-2">disputed</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="info-detail">
                        <h5 className="text-muted mb-1">Since</h5>
                        <p className="mb-0 fw-semibold">
                          {keyDetailData?.billingStatusUpdatedAt
                            ? moment.unix(Number(keyDetailData.billingStatusUpdatedAt)).format("MMM D, YYYY h:mm A")
                            : "—"}
                        </p>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="info-detail">
                        <h5 className="text-muted mb-1">Grace deadline</h5>
                        <p className="mb-0 fw-semibold">
                          {keyDetailData?.subscriptionGraceDeadlineAt
                            ? moment.unix(Number(keyDetailData.subscriptionGraceDeadlineAt)).format("MMM D, YYYY h:mm A")
                            : "—"}
                        </p>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="info-detail">
                        <h5 className="text-muted mb-1">Collect key by</h5>
                        <p className="mb-0 fw-semibold">
                          {keyDetailData?.keyRetrievalDeadlineAt
                            ? moment.unix(Number(keyDetailData.keyRetrievalDeadlineAt)).format("MMM D, YYYY h:mm A")
                            : "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                  {keyDetailData?.lastPaymentError?.message && (
                    <div className="alert alert-warning mb-3">
                      <i className="fa-solid fa-triangle-exclamation me-2" />
                      Last payment error: {keyDetailData.lastPaymentError.message}
                      {keyDetailData.lastPaymentError.code
                        ? ` (${keyDetailData.lastPaymentError.code})`
                        : ""}
                    </div>
                  )}

                  <div className="d-flex flex-wrap align-items-end gap-3 pt-2 border-top">
                    <div>
                      <label className="text-muted small d-block mb-1">Comp (free days)</label>
                      <div className="d-flex gap-2">
                        <input
                          type="number"
                          min={1}
                          max={365}
                          className="form-control form-control-sm"
                          style={{ width: 90 }}
                          value={compDays}
                          onChange={(e) => setCompDays(e.target.value)}
                        />
                        <button
                          className="btn btn-outline-primary btn-sm"
                          disabled={compLoading}
                          onClick={handleComp}
                        >
                          <i className="fa-solid fa-gift me-2" />
                          Comp
                        </button>
                      </div>
                    </div>
                    {["past_due", "grace", "restricted"].includes(
                      keyDetailData?.billingStatus
                    ) && (
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={payOutstandingLoading}
                        onClick={handlePayOutstanding}
                      >
                        <i className="fa-solid fa-money-bill-wave me-2" />
                        Pay outstanding invoice (owner's card)
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Key Information Card ── */}
              <div className="wrapper-card mb-4">
                <h3 className="mb-4">
                  <i className="fa-solid fa-circle-info me-2 text-muted" />
                  Key Information
                </h3>

                {/* Owner / Email / Address */}
                <div className="row gy-3 mb-4">
                  <div className="col-sm-6 col-lg-4">
                    <div className="info-detail">
                      <h5 className="text-muted mb-1">
                        <i className="fa-solid fa-user me-2" />Owner
                      </h5>
                      <p className="mb-0 fw-semibold">{keyDetailData?.ownerName || "—"}</p>
                    </div>
                  </div>
                  <div className="col-sm-6 col-lg-4">
                    <div className="info-detail">
                      <h5 className="text-muted mb-1">
                        <i className="fa-solid fa-envelope me-2" />Email
                      </h5>
                      <p className="mb-0 fw-semibold">{keyDetailData?.email || "—"}</p>
                    </div>
                  </div>
                  <div className="col-sm-6 col-lg-4">
                    <div className="info-detail">
                      <h5 className="text-muted mb-1">
                        <i className="fa-solid fa-location-dot me-2" />Address
                      </h5>
                      <p className="mb-0 fw-semibold">{keyDetailData?.address || "—"}</p>
                    </div>
                  </div>
                </div>

                {/* Keyring / Registration codes — shown when available */}
                {(keyDetailData?.keyringCode || keyDetailData?.registrationCode) && (
                  <div className="row gy-3 mb-4">
                    {keyDetailData?.keyringCode && (
                      <div className="col-sm-6">
                        <div className="info-detail">
                          <h5 className="text-muted mb-1">
                            <i className="fa-solid fa-link me-2" />Keyring ID
                          </h5>
                          <p className="mb-0 fw-semibold font-monospace">{keyDetailData.keyringCode}</p>
                        </div>
                      </div>
                    )}
                    {keyDetailData?.registrationCode && (
                      <div className="col-sm-6">
                        <div className="info-detail">
                          <h5 className="text-muted mb-1">
                            <i className="fa-solid fa-qrcode me-2" />Registration Code
                          </h5>
                          <p className="mb-0 fw-semibold font-monospace">{keyDetailData.registrationCode}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Subscription stat cards */}
                <div className="row gy-3">
                  <div className="col-sm-4">
                    <div className={`card h-100 ${getSubBorderClass()}`} style={{ borderWidth: 2 }}>
                      <div className="card-body text-center py-3">
                        <p className="text-muted mb-1 small">Subscription</p>
                        <h4 className={`mb-0 fw-bold ${getSubTextClass()}`}>{getSubLabel()}</h4>
                        {keyDetailData?.stripeSubscriptionStatus === "canceled" &&
                          keyDetailData?.subscriptionCancelString && (
                            <small className="text-muted d-block mt-1">
                              Cancelled{" "}
                              {moment.unix(keyDetailData.subscriptionCancelString).format("MMM D, YYYY")}
                            </small>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="card h-100 border" style={{ borderWidth: 2 }}>
                      <div className="card-body text-center py-3">
                        <p className="text-muted mb-1 small">
                          <i className="fa-regular fa-calendar me-1" />
                          {data?.status === 3 ? "Deleted On" : "Expires On"}
                        </p>
                        <h5 className="mb-0 fw-semibold">
                          {data?.status === 3
                            ? moment.unix(keyDetailData?.keyDeletionString).format("MMM D, YYYY")
                            : moment.unix(keyDetailData?.keySubscriptionExpiryString).format("MMM D, YYYY")}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="card h-100 border" style={{ borderWidth: 2 }}>
                      <div className="card-body text-center py-3">
                        <p className="text-muted mb-1 small">
                          <i className="fa-regular fa-calendar-check me-1" />Purchase Date
                        </p>
                        <h5 className="mb-0 fw-semibold">
                          {keyDetailData?.subscriptionAddedString
                            ? moment.unix(keyDetailData.subscriptionAddedString).format("MMM D, YYYY")
                            : <span className="text-muted">—</span>}
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Two-column row: Key Box + Key Host ── */}
              <div className="row gy-4 mb-4">

                {/* Key Box Detail */}
                <div className="col-lg-6">
                  <div className="wrapper-card h-100">
                    <h3 className="mb-4">
                      <i className="fa-solid fa-box me-2 text-muted" />Key Box Detail
                    </h3>
                    {keyDetailData?.boxDetail?.length > 0 ? (
                      keyDetailData.boxDetail.map((item: any, index: number) => (
                        <div key={index} className={index > 0 ? "mt-4 pt-4 border-top" : ""}>
                          <div className="row gy-3">
                            <div className="col-6">
                              <div className="info-detail">
                                <h5 className="text-muted mb-1">
                                  <i className="fa-solid fa-box-archive me-2" />Box ID
                                </h5>
                                <p className="mb-0 fw-semibold">{item.boxId}</p>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="info-detail">
                                <h5 className="text-muted mb-1">
                                  <i className="fa-solid fa-hashtag me-2" />Key Slot No.
                                </h5>
                                <p className="mb-0 fw-semibold">{item.keySlotNumber}</p>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="info-detail">
                                <h5 className="text-muted mb-1">
                                  <i className="fa-solid fa-link me-2" />Key Ring ID
                                </h5>
                                <p className="mb-0 fw-semibold font-monospace text-break">
                                  {item.keyringId || keyDetailData?.keyringCode || "—"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                        <i className="fa-solid fa-box-open fa-2x mb-2" />
                        <p className="mb-0">No key box details available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Key Host Detail */}
                <div className="col-lg-6">
                  <div className="wrapper-card h-100">
                    <h3 className="mb-4">
                      <i className="fa-solid fa-store me-2 text-muted" />Key Host
                    </h3>
                    <div className="info-detail mb-3">
                      <h5 className="text-muted mb-1">
                        <i className="fa-solid fa-building me-2" />Store Name
                      </h5>
                      {keyDetailData?.hostId ? (
                        <p
                          className="mb-0 fw-semibold fs-5"
                          style={{ cursor: "pointer", color: "#72bcb8" }}
                          title="Click to view key host details"
                          onClick={() =>
                            navigate(`/keyhostdetail/${keyDetailData.hostId}`, {
                              state: { id: keyDetailData.hostId },
                            })
                          }
                        >
                          {keyDetailData.hostName || "—"}
                          <i className="fa-solid fa-arrow-up-right-from-square ms-2 small" />
                        </p>
                      ) : (
                        <p className="mb-0 fw-semibold fs-5">{keyDetailData?.hostName || "—"}</p>
                      )}
                    </div>
                    <div className="info-detail mb-3">
                      <h5 className="text-muted mb-1">
                        <i className="fa-solid fa-phone me-2" />Phone Number
                      </h5>
                      <p className="mb-0 fw-semibold">
                        {keyDetailData?.hostCountryCode ? `(+${keyDetailData.hostCountryCode}) ` : ""}
                        {keyDetailData?.phoneNumber || "—"}
                      </p>
                    </div>
                    <div className="info-detail mb-3">
                      <h5 className="text-muted mb-1">
                        <i className="fa-solid fa-location-dot me-2" />Address
                      </h5>
                      <p className="mb-0">{keyDetailData?.hostAddress || "—"}</p>
                    </div>
                    {keyDetailData?.storeInfo && (
                      <div className="info-detail mb-3">
                        <h5 className="text-muted mb-1">
                          <i className="fa-solid fa-circle-info me-2" />Store Information
                        </h5>
                        <p className="mb-0">{keyDetailData.storeInfo}</p>
                      </div>
                    )}
                    <div className="info-detail">
                      <h5 className="text-muted mb-3">
                        <i className="fa-regular fa-clock me-2" />Hours of Operation
                      </h5>
                      <div>
                        {keyDetailData?.activeTimes?.map((time: any, index: number) => (
                          <div
                            key={index}
                            className="d-flex justify-content-between align-items-center py-2 border-bottom"
                          >
                            <span className="fw-semibold" style={{ minWidth: 100 }}>{time?.day}</span>
                            <span>
                              {time?.closed === false && time.startTime !== "" ? (
                                <span className="text-success">
                                  {moment.utc(time.startTime, "HH:mm").format("LT")} –{" "}
                                  {moment.utc(time.endTime, "HH:mm").format("LT")}
                                </span>
                              ) : (
                                <span className="text-danger">Closed</span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ════════════════════════════════════════════════════════
                  Inline Tab Panel — replaces all modals
              ════════════════════════════════════════════════════════ */}
              <div className="wrapper-card mb-4">

                {/* Tab strip */}
                <ul className="nav nav-pills mb-4 flex-wrap gap-1" role="tablist">
                  {tabs.map((tab) => (
                    <li className="nav-item" key={tab.id} role="presentation">
                      <button
                        className={`nav-link${activeTab === tab.id ? " active" : ""}`}
                        type="button"
                        role="tab"
                        onClick={() => handleTabChange(tab.id)}
                        style={
                          activeTab === tab.id
                            ? { backgroundColor: "#72bcb8", borderColor: "#72bcb8", color: "#fff" }
                            : {}
                        }
                      >
                        <i className={`fa-solid ${tab.icon} me-2`} />
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* ── Tab: Collection Activity ── */}
                {activeTab === "activity" && (
                  <div>
                    <p className="text-muted mb-3" style={{ fontSize: "0.875rem" }}>
                      Complete history of key events — registration, collection, and returns.
                      Sorted most-recent first.
                    </p>
                    <div className="table-blk">
                      <div className="table-responsive">
                        <table className="display border-0 w-100">
                          <thead>
                            <tr>
                              <th>Event</th>
                              <th>Collection Code</th>
                              <th>Code Label</th>
                              <th>Keyring Code</th>
                              <th>Key Host</th>
                              <th>Date &amp; Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {keyHistory?.data?.map((item: any, index: number) => {
                              // NOTE: All API responses are transformed by networking.service.ts
                              // which converts every snake_case key → camelCase.
                              // e.g. operation_type → operationType
                              //      key_collected_date → keyCollectedDate
                              //      key_returned_date  → keyReturnedDate
                              //      registered_at      → registeredAt
                              //      code_number        → codeNumber
                              //      code_label         → codeLabel
                              //      key_host_name      → keyHostName
                              //      guest_collect      → (value is a string, stays as-is inside the value)
                              //      guest_return       → (value stays as-is)

                              const opType: string = item?.operationType ?? "";

                              let eventLabel = "Unknown";
                              let eventCls   = "text-muted";
                              let eventIcon  = "fa-circle-question";
                              let eventDate: any = null;

                              if (opType === "registration") {
                                eventLabel = "Key Registered";
                                eventCls   = "text-primary";
                                eventIcon  = "fa-key";
                                // RegistrationAuditLog: registeredAt (unix ts), fallback to createdAt
                                eventDate  = item?.registeredAt ?? item?.createdAt;
                              } else if (opType === "guestCollect" || opType === "collection") {
                                eventLabel = "Collected";
                                eventCls   = "text-warning";
                                eventIcon  = "fa-hand";
                                eventDate  = item?.keyCollectedDate;
                              } else if (opType === "ownerReturnCode") {
                                eventLabel = "Owner Collected";
                                eventCls   = "text-info";
                                eventIcon  = "fa-user-check";
                                eventDate  = item?.keyCollectedDate;
                              } else if (opType === "ownerCollect") {
                                eventLabel = "Returned to Owner";
                                eventCls   = "text-success";
                                eventIcon  = "fa-circle-check";
                                eventDate  = item?.keyCollectedDate;
                              } else if (opType === "guestReturn" || opType === "keyReturn") {
                                eventLabel = "Returned";
                                eventCls   = "text-success";
                                eventIcon  = "fa-rotate-left";
                                eventDate  = item?.keyReturnedDate;
                              } else if (opType === "hostAuthorizationChange") {
                                eventLabel = "Authorized Host Changed";
                                eventCls   = "text-secondary";
                                eventIcon  = "fa-shuffle";
                                eventDate  = item?.createdAt;
                              } else {
                                // Legacy rows with no / unrecognised operationType —
                                // fall back to date-field heuristics
                                if (item?.keyReturnedDate) {
                                  eventLabel = "Returned";
                                  eventCls   = "text-success";
                                  eventIcon  = "fa-rotate-left";
                                  eventDate  = item.keyReturnedDate;
                                } else if (item?.keyCollectedDate) {
                                  eventLabel = "Collected";
                                  eventCls   = "text-warning";
                                  eventIcon  = "fa-hand";
                                  eventDate  = item.keyCollectedDate;
                                } else {
                                  eventDate = item?.createdAt;
                                }
                              }

                              // Unified date formatter — handles Unix timestamps AND ISO date strings
                              const fmtDate = (d: any) => {
                                if (!d) return <span className="text-muted">—</span>;
                                const m = typeof d === "number"
                                  ? moment.unix(d)
                                  : moment(d);
                                return m.isValid()
                                  ? <span>{m.format("MMM DD, YYYY h:mm A")}</span>
                                  : <span className="text-muted">—</span>;
                              };

                              // Separate: keyring code (registration) vs collection code (movements)
                              const isRegistrationEvent = opType === "registration";
                              const collectionCode = isRegistrationEvent ? null : item?.codeNumber;
                              const collectionLabel = isRegistrationEvent ? null : item?.codeLabel;
                              const keyringCode     = isRegistrationEvent ? item?.codeNumber : null;

                              return (
                                <tr key={index}>
                                  {/* Event */}
                                  <td>
                                    <span className={eventCls}>
                                      <i className={`fa-solid ${eventIcon} me-2`} />
                                      <strong>{eventLabel}</strong>
                                    </span>
                                  </td>

                                  {/* Collection Code — shown for movements, empty for registration */}
                                  <td>
                                    {collectionCode
                                      ? <span className="font-monospace small">{collectionCode}</span>
                                      : <span className="text-muted">—</span>}
                                  </td>

                                  {/* Code Label — friendly label on the collection code */}
                                  <td>
                                    {collectionLabel
                                      ? <span className="badge bg-light text-dark border">{collectionLabel}</span>
                                      : <span className="text-muted">—</span>}
                                  </td>

                                  {/* Keyring Code — shown for registration, empty for movements */}
                                  <td>
                                    {keyringCode
                                      ? <span className="font-monospace small">{keyringCode}</span>
                                      : <span className="text-muted">—</span>}
                                  </td>

                                  {/* Key Host store — clickable if we have the ID */}
                                  <td>
                                    {item?.keyHostName ? (
                                      item?.keyHostId ? (
                                        <span
                                          className="small"
                                          style={{ cursor: "pointer", color: "#72bcb8" }}
                                          title="View key host details"
                                          onClick={() =>
                                            navigate(`/keyhostdetail/${item.keyHostId}`, {
                                              state: { id: item.keyHostId },
                                            })
                                          }
                                        >
                                          {item.keyHostName}
                                          <i className="fa-solid fa-arrow-up-right-from-square ms-1 small" />
                                        </span>
                                      ) : (
                                        <span className="small">{item.keyHostName}</span>
                                      )
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>

                                  {/* Date & Time */}
                                  <td className="small">{fmtDate(eventDate)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {keyHistory?.data?.length === 0 && (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                          <i className="fa-solid fa-history fa-2x mb-2" />
                          <h5 className="mb-1">No Activity Yet</h5>
                          <p className="mb-0 small">No key collection or return records found</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Tab: Collection Codes (New System) ── */}
                {activeTab === "codes" && (
                  <div>
                    <p className="text-muted mb-3" style={{ fontSize: "0.875rem" }}>
                      All collection codes for this key (new system)
                    </p>

                    {/* Search + Filter */}
                    <div className="row mb-4 gy-2">
                      <div className="col-md-6">
                        <div className="input-group">
                          <span className="input-group-text">
                            <i className="fa-solid fa-search" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search by code or label…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select"
                          value={statusFilter}
                          onChange={(e) => handleStatusFilterChange(e.target.value)}
                        >
                          <option value="">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="expired">Expired</option>
                          <option value="revoked">Revoked</option>
                          <option value="depleted">Depleted</option>
                        </select>
                      </div>
                    </div>

                    {loadingCodes ? (
                      <div className="d-flex justify-content-center align-items-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading…</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Summary stat cards */}
                        {collectionCodesData?.data?.summary && (
                          <div className="row gy-3 mb-4">
                            {[
                              { label: "Total",   value: collectionCodesData.data.summary.total,   border: "",              textCls: ""             },
                              { label: "Active",  value: collectionCodesData.data.summary.active,  border: "border-success", textCls: "text-success" },
                              { label: "Expired", value: collectionCodesData.data.summary.expired, border: "border-warning", textCls: "text-warning" },
                              { label: "Revoked", value: collectionCodesData.data.summary.revoked, border: "border-danger",  textCls: "text-danger"  },
                            ].map((s) => (
                              <div key={s.label} className="col-6 col-md-3">
                                <div className={`card text-center ${s.border}`}>
                                  <div className="card-body py-3">
                                    <p className="mb-1 small text-muted">{s.label}</p>
                                    <h3 className={`mb-0 ${s.textCls}`}>{s.value}</h3>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="table-blk">
                          <div className="table-responsive">
                            <table className="display border-0 w-100">
                              <thead>
                                <tr>
                                  <th>Code</th>
                                  <th>Label</th>
                                  <th>Type</th>
                                  <th>Status</th>
                                  <th>Usage</th>
                                  <th>Expires</th>
                                  <th>Created</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredCodes?.map((code: any, index: number) => (
                                  <tr key={index}>
                                    <td><strong className="font-monospace">{code.code}</strong></td>
                                    <td>{code.label || <span className="text-muted">—</span>}</td>
                                    <td>
                                      {code.isReusable
                                        ? <span className="badge bg-info">Multi-use {code.maxUses ? `(${code.maxUses})` : "(∞)"}</span>
                                        : <span className="badge bg-secondary">Single-use</span>}
                                    </td>
                                    <td>
                                      <span className={code.status === "active" ? "c-pill c-pill--success" : "c-pill c-pill--danger"}>
                                        {code.status.charAt(0).toUpperCase() + code.status.slice(1)}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="badge bg-primary">
                                        {code.timesUsed}{code.isReusable && code.maxUses ? ` / ${code.maxUses}` : ""}
                                      </span>
                                    </td>
                                    <td>
                                      {code.isPermanent
                                        ? <span className="badge bg-success">Never</span>
                                        : code.expiresAt
                                          ? moment.unix(code.expiresAt).format("MMM DD, YYYY")
                                          : <span className="text-muted">N/A</span>}
                                    </td>
                                    <td>{moment(code.createdAt).format("MMM DD, YYYY")}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {filteredCodes?.length === 0 && (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                              <i className="fa-solid fa-qrcode fa-2x mb-2" />
                              <h5 className="mb-1">No Collection Codes</h5>
                              <p className="mb-0 small">No collection codes have been created for this key yet</p>
                            </div>
                          )}
                        </div>

                        {/* Pagination */}
                        {collectionCodesData?.data?.pagination && (
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1 || loadingCodes}
                            >
                              <i className="fa-solid fa-chevron-left me-1" />Previous
                            </button>
                            <p className="text-muted mb-0 small">
                              Page {collectionCodesData.data.pagination.page} of {collectionCodesData.data.pagination.pages}
                              {" "}({collectionCodesData.data.pagination.total} codes total)
                              {searchQuery && ` · ${filteredCodes.length} filtered`}
                            </p>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage >= collectionCodesData.data.pagination.pages || loadingCodes}
                            >
                              Next <i className="fa-solid fa-chevron-right ms-1" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── Tab: Transactions ── */}
                {activeTab === "transactions" && (
                  <div>
                    <p className="text-muted mb-3" style={{ fontSize: "0.875rem" }}>
                      Subscription billing history for this key
                    </p>
                    <div className="table-blk">
                      <div className="table-responsive">
                        <table className="display border-0 w-100">
                          <thead>
                            <tr>
                              <th>Plan Name</th>
                              <th>Period</th>
                              <th>Actual Amount</th>
                              <th>Paid Amount</th>
                              <th>Stripe Subscription ID</th>
                              <th>Expiry</th>
                            </tr>
                          </thead>
                          <tbody>
                            {keyDetailData?.transactionLogs?.map((item: any, idx: number) => (
                              <tr key={idx}>
                                <td>{item?.planName}</td>
                                <td>{item?.planType}</td>
                                <td>{`${getSymbolFromCurrency(item?.currency)}${item?.actualAmount ?? "0"}`}</td>
                                <td>{`${getSymbolFromCurrency(item?.currency)}${item?.paidAmount ?? "0"}`}</td>
                                <td><span className="font-monospace small">{item?.stripeSubscriptionId}</span></td>
                                <td>{moment.unix(keyDetailData?.keySubscriptionExpiryString).format("l")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {keyDetailData?.transactionLogs?.length === 0 && (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                          <i className="fa-solid fa-receipt fa-2x mb-2" />
                          <h5 className="mb-1">No Transactions</h5>
                          <p className="mb-0 small">No billing records found for this key</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
              {/* end inline tab panel */}

            </div>
          </section>
        </div>
      </CustomLaoder>

      {/* ── Delete Key Modal ── */}
      {showDeleteModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title text-danger">
                  <i className="fa-solid fa-triangle-exclamation me-2" />
                  Delete Key
                </h5>
                <button type="button" className="btn-close" onClick={closeDeleteModal} disabled={deleteSubmitting} />
              </div>
              <div className="modal-body">
                <p className="mb-1">
                  You are about to permanently delete <strong>{keyDetailData?.name}</strong>.
                </p>
                <div className="alert alert-warning py-2 small mb-3">
                  <i className="fa-solid fa-bolt me-1" />
                  This will <strong>immediately cancel</strong> the owner's Stripe subscription and cannot be undone.
                </div>

                {keyDetailData?.status === 1 && (
                  <div className="alert alert-danger py-2 small mb-3">
                    <i className="fa-solid fa-box me-1" />
                    This key is currently <strong>in a keybox</strong>. The physical key will remain in the box — the key host must be asked to retrieve it.
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Reason for deletion <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter the reason for deleting this key…"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    disabled={deleteSubmitting}
                  />
                </div>

                {keyDetailData?.status === 1 && (
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="keyHostNotifiedDetail"
                      checked={keyHostNotified}
                      onChange={(e) => setKeyHostNotified(e.target.checked)}
                      disabled={deleteSubmitting}
                    />
                    <label className="form-check-label small" htmlFor="keyHostNotifiedDetail">
                      I have contacted the key host to retrieve the physical key <span className="text-danger">*</span>
                    </label>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top-0 pt-0">
                <button className="btn btn-secondary" onClick={closeDeleteModal} disabled={deleteSubmitting}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={
                    deleteSubmitting ||
                    !deleteReason.trim() ||
                    (keyDetailData?.status === 1 && !keyHostNotified)
                  }
                >
                  {deleteSubmitting
                    ? <><span className="spinner-border spinner-border-sm me-2" />Deleting…</>
                    : "Delete Key"
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Authorized Host Modal ── */}
      {showChangeHostModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title">
                  <i className="fa-solid fa-shuffle me-2" />
                  Change Authorized Host
                </h5>
                <button type="button" className="btn-close" onClick={closeChangeHostModal} disabled={changeHostSubmitting} />
              </div>
              <div className="modal-body">
                <p className="mb-3 small text-muted">
                  Only the currently authorized host can accept a registration or return for this
                  key. This changes it immediately - the target host does not need to approve.
                </p>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Search Key Hosts</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by store name…"
                    value={changeHostSearch}
                    onChange={(e) => setChangeHostSearch(e.target.value)}
                    disabled={changeHostSubmitting}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    New Authorized Host <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={selectedHostId}
                    onChange={(e) => setSelectedHostId(e.target.value)}
                    disabled={changeHostSubmitting}
                  >
                    <option value="">Select a key host…</option>
                    {keyHostOptions?.data?.keyHosts?.map((host: any) => (
                      <option key={host._id} value={host._id}>
                        {host.storeName || host.store_name || host._id}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Reason <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter the reason for this change…"
                    value={changeHostReason}
                    onChange={(e) => setChangeHostReason(e.target.value)}
                    disabled={changeHostSubmitting}
                  />
                </div>
              </div>
              <div className="modal-footer border-top-0 pt-0">
                <button className="btn btn-secondary" onClick={closeChangeHostModal} disabled={changeHostSubmitting}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleConfirmChangeHost}
                  disabled={changeHostSubmitting || !selectedHostId || !changeHostReason.trim()}
                >
                  {changeHostSubmitting
                    ? <><span className="spinner-border spinner-border-sm me-2" />Changing…</>
                    : "Change Host"
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyManagementDetailScreen;
